import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useNotification } from '../../context/NotificationContext';
import { Tooltip } from '@mui/material';
import {
  PersonOutline as PersonIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Send as SendIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';

import { useUser, ROLES } from '../../context/UserContext';
import { useSelectedOrganization } from '../../hooks/useSelectedOrganization';
import './Settings.css';

const UserManagement = ({ userOrganizationId }) => {
  const { getAccessTokenSilently } = useAuth0();
  const { isAdmin, isSuperAdmin, userInfo, isLoading: isUserLoading } = useUser();
  const navigate = useNavigate();
  const activeOrganizationId = useSelectedOrganization(userOrganizationId);
  const notification = useNotification();

  // Tab state
  const [activeTab, setActiveTab] = useState('users');

  // Users state
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Invitations state
  const [invitations, setInvitations] = useState([]);

  useEffect(() => {
    if (!isUserLoading && !isAdmin()) {
      notification.error('You do not have permission to access this page');
      navigate('/dashboard');
    }
  }, [isAdmin, isUserLoading, navigate, notification]);

  const fetchUsers = async () => {
    if (!activeOrganizationId) return;

    try {
      setIsLoading(true);
      const token = await getAccessTokenSilently();
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_DOMAIN}/api/users/organization/${activeOrganizationId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      notification.error('Failed to load users');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchInvitations = async () => {
    if (!activeOrganizationId) return;

    try {
      const token = await getAccessTokenSilently();
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_DOMAIN}/api/users/invitations`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInvitations(response.data);
    } catch (error) {
      console.error('Error fetching invitations:', error);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchInvitations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOrganizationId, getAccessTokenSilently]);

  const handleInviteClick = () => {
    navigate('/settings/users/new');
  };

  const handleEditClick = (user) => {
    navigate(`/settings/users/${user.id || user._id}`);
  };

  const handleResendInvitation = async (invitationId) => {
    try {
      const token = await getAccessTokenSilently();
      await axios.post(
        `${process.env.REACT_APP_SERVER_DOMAIN}/api/users/invitations/${invitationId}/resend`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      notification.success('Invitation resent successfully');
      fetchInvitations();
    } catch (error) {
      console.error('Error resending invitation:', error);
      notification.error(error.response?.data?.error || 'Failed to resend invitation');
    }
  };

  const handleRevokeInvitation = async (invitationId) => {
    try {
      const token = await getAccessTokenSilently();
      await axios.delete(
        `${process.env.REACT_APP_SERVER_DOMAIN}/api/users/invitations/${invitationId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      notification.success('Invitation revoked');
      fetchInvitations();
    } catch (error) {
      console.error('Error revoking invitation:', error);
      notification.error(error.response?.data?.error || 'Failed to revoke invitation');
    }
  };

  const handleToggleUserStatus = async (user) => {
    const userId = user.id || user._id;
    const newStatus = user.status === 'active' ? 'inactive' : 'active';
    const endpoint = newStatus === 'inactive' ? 'deactivate' : 'reactivate';

    try {
      const token = await getAccessTokenSilently();
      await axios.post(
        `${process.env.REACT_APP_SERVER_DOMAIN}/api/users/${userId}/${endpoint}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      notification.success(`User ${newStatus === 'active' ? 'reactivated' : 'deactivated'} successfully`);
      fetchUsers();
    } catch (error) {
      console.error('Error toggling user status:', error);
      notification.error(error.response?.data?.error || 'Failed to update user status');
    }
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case ROLES.SUPER_ADMIN: return 'super-admin';
      case ROLES.ADMIN: return 'admin';
      case ROLES.AGENT: return 'agent';
      default: return '';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case ROLES.SUPER_ADMIN: return 'Super Admin';
      case ROLES.ADMIN: return 'Admin';
      case ROLES.AGENT: return 'Agent';
      default: return role;
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'active': return 'active';
      case 'inactive': return 'inactive';
      case 'pending': return 'pending';
      default: return '';
    }
  };

  const canEditUser = (targetUser) => {
    const targetId = targetUser.id || targetUser._id;
    if (targetId === userInfo?.id) return false;
    if (isSuperAdmin()) return true;
    if (isAdmin() && targetUser.role === ROLES.AGENT) return true;
    return false;
  };

  const canDeactivateUser = (targetUser) => {
    const targetId = targetUser.id || targetUser._id;
    if (targetId === userInfo?.id) return false;
    if (isSuperAdmin() && targetUser.role !== ROLES.SUPER_ADMIN) return true;
    if (isAdmin() && targetUser.role === ROLES.AGENT) return true;
    return false;
  };

  const pendingInvitationsCount = invitations.filter(i => i.status === 'pending').length;

  if (isUserLoading || isLoading) {
    return (
      <div className="settings-page">
        <div className="settings-page-header">
          <div className="settings-page-header-left">
            <h1 className="settings-page-title">Users</h1>
            <p className="settings-page-subtitle">Manage team members and their permissions</p>
          </div>
        </div>
        <div className="settings-page-content">
          <div className="settings-loading">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-page">
      {/* Page Header */}
      <div className="settings-page-header">
        <div className="settings-page-header-left">
          <h1 className="settings-page-title">Users</h1>
          <p className="settings-page-subtitle">Manage team members and their permissions</p>
        </div>
        <div className="settings-page-header-right">
          <button className="settings-btn primary" onClick={handleInviteClick}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Invite New User
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="settings-tabs">
        <button
          className={`settings-tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          Users
          <span className="settings-tab-count">{users.length}</span>
        </button>
        <button
          className={`settings-tab ${activeTab === 'invitations' ? 'active' : ''}`}
          onClick={() => setActiveTab('invitations')}
        >
          Invitations
          <span className="settings-tab-count">{pendingInvitationsCount}</span>
        </button>
      </div>

      {/* Page Content */}
      <div className="settings-page-content">
        {activeTab === 'users' && (
          <div className="settings-card">
            <table className="settings-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Assigned Locations</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => {
                  const userId = user.id || user._id;
                  const isCurrentUser = userId === userInfo?.id;
                  const isInactive = user.status === 'inactive';

                  return (
                    <tr key={userId} style={{ opacity: isInactive ? 0.6 : 1 }}>
                      <td>
                        <div className="settings-user-info">
                          {user.picture ? (
                            <img src={user.picture} alt="" className="settings-user-avatar" />
                          ) : (
                            <div className="settings-user-avatar-icon">
                              <PersonIcon style={{ fontSize: 18 }} />
                            </div>
                          )}
                          <span className="settings-user-name">
                            {user.firstname || user.given_name} {user.lastname || user.family_name}
                            {isCurrentUser && <span className="settings-user-you-badge">(You)</span>}
                          </span>
                        </div>
                      </td>
                      <td>{user.email}</td>
                      <td>
                        <span className={`settings-role-badge ${getRoleBadgeClass(user.role)}`}>
                          {getRoleLabel(user.role)}
                        </span>
                      </td>
                      <td>
                        <span className={`settings-status-badge ${getStatusBadgeClass(user.status || 'active')}`}>
                          {user.status || 'active'}
                        </span>
                      </td>
                      <td>
                        {user.role === ROLES.AGENT ? (
                          user.assigned_locations?.length > 0 ? (
                            <div className="settings-location-chips">
                              {user.assigned_locations.map(loc => (
                                <span key={loc._id || loc.id} className="settings-location-chip">
                                  {loc.store_name}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="settings-no-locations">No locations assigned</span>
                          )
                        ) : (
                          <span className="settings-all-locations">All locations</span>
                        )}
                      </td>
                      <td>
                        <div className="settings-table-actions">
                          {canEditUser(user) && (
                            <Tooltip title="Edit user">
                              <button className="settings-action-btn primary" onClick={() => handleEditClick(user)}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                </svg>
                              </button>
                            </Tooltip>
                          )}
                          {canDeactivateUser(user) && (
                            <Tooltip title={user.status === 'active' ? 'Deactivate user' : 'Reactivate user'}>
                              <button
                                className={`settings-action-btn ${user.status === 'active' ? 'danger' : 'success'}`}
                                onClick={() => handleToggleUserStatus(user)}
                              >
                                {user.status === 'active' ? (
                                  <BlockIcon style={{ fontSize: 18 }} />
                                ) : (
                                  <CheckCircleIcon style={{ fontSize: 18 }} />
                                )}
                              </button>
                            </Tooltip>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {users.length === 0 && (
                  <tr>
                    <td colSpan="6">
                      <div className="settings-empty-state">
                        <p className="settings-empty-state-text">No users found.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'invitations' && (
          <div className="settings-card">
            <table className="settings-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Expires</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {invitations.map((invitation) => {
                  const invitationId = invitation.id || invitation._id;
                  return (
                    <tr key={invitationId}>
                      <td>{invitation.email}</td>
                      <td>{invitation.firstname} {invitation.lastname}</td>
                      <td>
                        <span className={`settings-role-badge ${getRoleBadgeClass(invitation.role)}`}>
                          {getRoleLabel(invitation.role)}
                        </span>
                      </td>
                      <td>
                        <span className={`settings-status-badge ${getStatusBadgeClass(invitation.status)}`}>
                          {invitation.status}
                        </span>
                      </td>
                      <td>
                        {invitation.expires_at
                          ? new Date(invitation.expires_at).toLocaleDateString()
                          : '-'
                        }
                      </td>
                      <td>
                        {invitation.status === 'pending' ? (
                          <div className="settings-table-actions">
                            <Tooltip title="Resend invitation">
                              <button className="settings-action-btn primary" onClick={() => handleResendInvitation(invitationId)}>
                                <SendIcon style={{ fontSize: 18 }} />
                              </button>
                            </Tooltip>
                            <Tooltip title="Revoke invitation">
                              <button className="settings-action-btn danger" onClick={() => handleRevokeInvitation(invitationId)}>
                                <DeleteIcon style={{ fontSize: 18 }} />
                              </button>
                            </Tooltip>
                          </div>
                        ) : (
                          <span style={{ color: '#9ca3af' }}>-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {invitations.length === 0 && (
                  <tr>
                    <td colSpan="6">
                      <div className="settings-empty-state">
                        <p className="settings-empty-state-text">No invitations found.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

      </div>
    </div>
  );
};

export default UserManagement;
